import React, { useState } from 'react';

import '../inventoryHistoryV2.css';
import './filterComponents.css';

const MAX_VISIBLE_CHIPS = 4;

function FilterChipsContainerTW({ activeFilters = [], filterConfigs = [], onChipClick, onDelete }) {
    // Function to get filter config by key
    const getFilterConfigByKey = (key) => filterConfigs.find((config) => config.filterKey === key);

    const [showLess, setShowLess] = useState(true);

    // If there are no active filters, render nothing
    if (activeFilters.length === 0) {
        return (
            <div className="p-3 flex overflow-x-auto items-center gap-2 min-h-[56px]">
                <span className="text-gray-500 italic">No filters applied</span>
            </div>
        );
    }

    // Create a map of filter types to their order in filterConfigs
    const filterOrder = filterConfigs.reduce((acc, config, index) => {
        acc[config.type] = index;
        return acc;
    }, {});

    // Sort activeFilters based on the order in filterConfigs
    const sortedFilters = [...activeFilters].sort((a, b) => {
        const orderA = filterOrder[a.type] ?? Number.MAX_VALUE;
        const orderB = filterOrder[b.type] ?? Number.MAX_VALUE;
        return orderA - orderB;
    });

    const visibleFilters = showLess ? sortedFilters.slice(0, MAX_VISIBLE_CHIPS) : sortedFilters;
    const remainingCount = sortedFilters.length - MAX_VISIBLE_CHIPS;

    const handleShowMore = () => {
        setShowLess(false);
    };

    const handleShowLess = () => {
        setShowLess(true);
    };

    const handleClearAll = () => {
        // Send all deletable filters at once to onDelete
        const deletableFilters = sortedFilters.filter((filter) => {
            const config = getFilterConfigByKey(filter.filterKey);
            return config && config.deletable;
        });

        if (deletableFilters.length > 0) {
            onDelete(deletableFilters);
        }
    };

    const handleChipClick = (filter) => {
        if (onChipClick) {
            onChipClick(filter);
        }
    };

    return (
        <div className="p-3 flex flex-wrap items-center gap-2 min-h-[56px] inventory-history-v2">
            {visibleFilters.map((filter, index) => {
                const config = getFilterConfigByKey(filter.filterKey);
                // Skip if no config found or no chip label
                if (!config || !filter.chipLabel) {
                    return null;
                }

                return (
                    <div
                        key={`${filter.filterKey}-${index}`}
                        className={`filter-components filter-chip ${config.deletable ? 'deletable' : 'non-deletable'} cursor-pointer`}
                        onClick={() => handleChipClick(filter)}
                    >
                        <span>{filter.chipLabel}</span>
                        {config.deletable && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent chip click when delete button is clicked
                                    onDelete(filter);
                                }}
                                className="ml-1 p-1 rounded-full hover:bg-[var(--shared-grey)] focus:outline-none"
                                aria-label={`Remove ${filter.chipLabel} filter`}
                            >
                                <span className="h-4 w-4 flex items-center justify-center text-white">&#10005;</span>
                            </button>
                        )}
                    </div>
                );
            })}

            {/* Show More button */}
            {showLess && remainingCount > 0 && (
                <button
                    onClick={handleShowMore}
                    className="filter-components more-less-chip cursor-pointer"
                    title={sortedFilters
                        .slice(MAX_VISIBLE_CHIPS)
                        .map((f) => f.chipLabel)
                        .join(', ')}
                >
                    +{remainingCount} more
                </button>
            )}

            {/* Show Less button */}
            {!showLess && sortedFilters.length > MAX_VISIBLE_CHIPS && (
                <button onClick={handleShowLess} className="filter-components more-less-chip cursor-pointer">
                    Show less
                </button>
            )}

            {/* Clear All button */}
            {sortedFilters.some((filter) => {
                const config = getFilterConfigByKey(filter.filterKey);
                return config && config.deletable;
            }) && (
                <button onClick={handleClearAll} className="filter-components clear-chip cursor-pointer">
                    Clear
                </button>
            )}
        </div>
    );
}

export { FilterChipsContainerTW };
